import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { SignedIn, SignedOut, SignInButton } from '@clerk/clerk-react';
import {
  ArrowRight,
  CalendarCheck,
  Target,
  ListVideo,
  BarChart3,
  Calendar,
  CheckCircle2,
  Quote,
  ChevronDown,
  Send,
  Mail,
  CirclePlay,
  FileQuestion,
  Users,
} from 'lucide-react';

import { supabase } from '../utils/supabase';
import { blogPosts } from '../data/blogPosts';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { cn } from '../utils/cn';

const logo = '/logo_miyo.webp';

// ─── Duyurular (Bildirim Bitmiyo) ────────────────────────────────────────────
interface Announcement {
  id: string;
  title: string;
  image_url: string;
  link_url: string;
  is_active: boolean;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
}

// ─── Bitmiyo ekosistemi (üç platform) ────────────────────────────────────────
// Açıklamalar üç sitenin canlı içeriğiyle birebir tutulur — abartı/uydurma özellik yok.
const ECOSYSTEM = [
  {
    icon: CalendarCheck,
    name: 'Bitmiyo Plan',
    domain: 'bitmiyo.com.tr',
    href: '/anasayfa',
    external: false,
    description: 'YouTube oynatma listelerini sınav tarihine göre günlük çalışma programına çevirir.',
    points: ['Otomatik program', 'Günlük hedef takibi', 'İlerleme analizi'],
    color: 'from-emerald-500 to-teal-500',
    badgeBg: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
    hover: 'hover:border-emerald-500/40',
  },
  {
    icon: FileQuestion,
    name: 'Bitmiyo Soru',
    domain: 'soru.bitmiyo.com.tr',
    href: 'https://soru.bitmiyo.com.tr',
    external: true,
    description: 'Soru bankası ve test hazırlama portalı.',
    points: ['Soru bankası', 'Test oluşturma', 'Öğretmen stüdyosu'],
    color: 'from-sky-500 to-indigo-500',
    badgeBg: 'bg-sky-500/10 text-sky-600 dark:text-sky-400 border-sky-500/20',
    hover: 'hover:border-sky-500/40',
  },
  {
    icon: Users,
    name: 'Bitmiyo Sınıf',
    domain: 'sinif.bitmiyo.com.tr',
    href: 'https://sinif.bitmiyo.com.tr',
    external: true,
    description: 'Canlı ders takibi, ödev yönetimi ve koçluk sistemi.',
    points: ['Sınıf yönetimi', 'Ödev takibi', 'Koçluk paneli'],
    color: 'from-purple-500 to-indigo-500',
    badgeBg: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
    hover: 'hover:border-purple-500/40',
  },
];

// ─── "Nasıl Çalışır" adımları ────────────────────────────────────────────────
const HOW_IT_WORKS = [
  {
    icon: ListVideo,
    title: 'Oynatma Listesini Ekle',
    description:
      "Takip ettiğin YouTube kanalının ders playlist linkini yapıştır. Videolar süresiyle tek tek listelenir.",
    color: 'from-sky-500 to-indigo-500',
    badgeBg: 'bg-sky-500/10 text-sky-600 dark:text-sky-400 border-sky-500/20',
  },
  {
    icon: Target,
    title: 'Sınav Tarihini Belirle',
    description:
      'Sınava kalan gün sayısına göre videoları günlere dağıtır ve günlük çalışma temposunu otomatik hesaplar.',
    color: 'from-emerald-500 to-teal-500',
    badgeBg: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
  },
  {
    icon: CalendarCheck,
    title: 'Günlük Hedeflerini İzle',
    description:
      '"Bugünün Hedefi" panelinden izlemen gereken videoları gör; izledikçe işaretle ve serini koru.',
    color: 'from-purple-500 to-indigo-500',
    badgeBg: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
  },
  {
    icon: BarChart3,
    title: 'İlerlemeni Analiz Et',
    description:
      'Tamamlanan saat, tamamlama oranı ve çalışma serisi grafikleriyle motivasyonunu her gün yüksek tut.',
    color: 'from-amber-500 to-rose-500',
    badgeBg: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
  },
] as const;

// ─── Öne Çıkan Özellikler ───────────────────────────────────────────────────
const FEATURES = [
  {
    id: 'bolucu',
    img: '/icons/3d/high_voltage_3d.webp',
    imgAlt: 'Şimşek',
    title: 'Otomatik Ders Bölücü',
    shortDesc: '100+ videolu oynatma listesini dakikalar içinde günlere böler.',
    accent: 'bg-amber-500/20 text-amber-400 border-amber-500/40',
    glowColor: 'text-amber-400',
    previewBadge: 'Akıllı Bölümleme Demo',
    previewTitle: 'TYT Matematik Kampı (60 Video)',
    previewSubtitle: 'Sınava 15 Gün Kaldı → Günde 4 Video',
  },
  {
    id: 'dinamik',
    img: '/icons/3d/alarm_clock_3d.webp',
    imgAlt: 'Alarm saati',
    title: 'Dinamik Hedef Güncelleme',
    shortDesc: 'Bir gün aksatsan bile sistem kalan günleri baştan hesaplar.',
    accent: 'bg-sky-500/20 text-sky-400 border-sky-500/40',
    glowColor: 'text-sky-400',
    previewBadge: 'Otomatik Dengeleme Demo',
    previewTitle: 'Dün Aksatılan: 2 Video',
    previewSubtitle: 'Sistem Kalan 5 Güne Otomatik Dağıttı (+0.4 v/gün)',
  },
  {
    id: 'masam',
    img: '/icons/3d/open_book_3d.webp',
    imgAlt: 'Açık kitap',
    title: 'Çalışma Masam & Odak Modu',
    shortDesc: 'Pomodoro zamanlayıcı, Lo-Fi müzik ve dikkati dağıtmayan ekran.',
    accent: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40',
    glowColor: 'text-emerald-400',
    previewBadge: 'Özel Odak Alanı Demo',
    previewTitle: 'Çalışma Masam (Pomodoro & Lo-Fi)',
    previewSubtitle: '25 Dk Odaklanma • YouTube Odak Modu • Tam Ekran',
  },
  {
    id: 'seri',
    img: '/icons/3d/fire_3d.webp',
    imgAlt: 'Ateş',
    title: 'Çalışma Serisi Takibi',
    shortDesc: 'Her gün hedefini tamamlayarak çalışma serini ve disiplinini artır.',
    accent: 'bg-rose-500/20 text-rose-400 border-rose-500/40',
    glowColor: 'text-rose-400',
    previewBadge: 'Motivasyon Sayacı Demo',
    previewTitle: '🔥 14 Gün Kesintisiz Seri!',
    previewSubtitle: 'Haftalık hedefler %92 oranında tamamlandı',
  },
] as const;

// ─── Sıkça Sorulan Sorular (SSS / FAQ) ───────────────────────────────────────
const FAQS = [
  {
    question: 'Herhangi bir saklı veya gizli ücret var mı?',
    answer:
      'Hayır, kesinlikle yok. Bitmiyo tamamen ücretsiz bir platformdur. Herhangi bir abonelik ücreti, premium sürüm veya gizli bir maliyet bulunmamaktadır.',
  },
  {
    question: 'Hangi sınavlar için ders programı oluşturabilirim?',
    answer:
      'YKS (TYT-AYT), LGS, KPSS, ALES, DGS, YDS ve okul sınavları dahil olmak üzere YouTube üzerinde oynatma listesi bulunan tüm dersler için çalışma takvimi hazırlayabilirsiniz.',
  },
  {
    question: 'Bir gün ders çalışmayı aksatırsam programım ne olur?',
    answer:
      'Bitmiyo\'nun akıllı dinamik hedef güncelleme özelliği sayesinde izleyemediğiniz videolar kalan günlerinize otomatik olarak yeniden dağıtılır.',
  },
  {
    question: 'YouTube oynatma listemi nasıl eklerim?',
    answer:
      'Takip ettiğiniz eğitmenin YouTube oynatma listesi (playlist) linkini kopyalayıp "Yeni Plan Oluştur" alanına yapıştırmanız ve hedef tarihinizi seçmeniz yeterlidir.',
  },
  {
    question: 'Giriş yapmadan ders planı hazırlayabilir miyim?',
    answer:
      'Ders videolarını incelemek ve plan taslağı oluşturmak serbesttir. Çalışma serinizi, izleme ilerlemenizi ve hedeflerinizi kaydetmek için saniyeler içinde ücretsiz hesap oluşturabilirsiniz.',
  },
  {
    question: 'Kendi özel çalışma takvimimi veya günlük izleme saatimi belirleyebilir miyim?',
    answer:
      'Evet! Günlük maksimum izleme sürenizi veya haftalık dinlenme günlerinizi (örneğin Pazar günleri mola) özel planlama ayarlarından kolayca belirleyebilirsiniz.',
  },
  {
    question: 'Mobil cihazlarda ve tablette kullanabilir miyim?',
    answer:
      'Evet, Bitmiyo PWA (Progressive Web App) altyapısı sayesinde tüm iOS, Android akıllı telefon ve tabletlerde uygulama rahatlığında sorunsuz çalışır.',
  },
] as const;

// ─── Öğrenci Yorumları ───────────────────────────────────────────────────────
// Not: Bu yorumlar temsili/placeholder içeriktir, gerçek kullanıcı verisi değildir.
// İsim biçimi: ad + soyadın ilk 2 harfi + soyadın kalan harf sayısı kadar yıldız.
const TESTIMONIALS = [
  {
    name: 'Mehmet Ar****',
    meta: 'YKS Adayı',
    text: "Playlist'i ekleyip gerisini Bitmiyo'ya bıraktım, çalışma düzenim oturdu. Artık hangi videoyu izleyeceğimi düşünerek vakit kaybetmiyorum.",
  },
  {
    name: 'Zeynep Ka**',
    meta: 'LGS Adayı',
    text: 'Sınava kalan gün sayısını görünce günlük hedefi ertelemek zorlaşıyor. Benim için en çok işe yarayan kısım bu oldu.',
  },
  {
    name: 'Berkay Öz*****',
    meta: 'TYT-AYT Hazırlık',
    text: 'Defterlerde tuttuğum video listesini bıraktım. Program otomatik güncellendiği için geri kaldığımda paniklemiyorum.',
  },
  {
    name: 'Elif De***',
    meta: 'KPSS Adayı',
    text: 'Uzun bir konu anlatım listesini günlere bölmek elle çok yorucuydu. Şimdi tek linkle hallediyorum.',
  },
  {
    name: 'Furkan Yı****',
    meta: 'Mezun / YKS',
    text: 'Günlük hedefleri tamamlayınca serinin devam etmesi hoşuma gidiyor, çalışmaya oturmak kolaylaştı.',
  },
  {
    name: 'Selin Ay***',
    meta: 'Lise Öğrencisi',
    text: 'Hangi konuda ne kadar ilerlediğimi görebilmek en sevdiğim kısım. Eksiklerimi fark etmek kolaylaştı.',
  },
] as const;

// ─── Header ──────────────────────────────────────────────────────────────────
export function LandingHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const isOnLanding = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const sectionLinkProps = (sectionId: string) =>
    isOnLanding ? { href: `#${sectionId}` } : { href: `/#${sectionId}` };

  return (
    <header className="sticky top-0 z-50 w-full transition-all duration-300">
      <div
        className={cn(
          'mx-auto flex items-center justify-between gap-4 transition-all duration-300 ease-out',
          isScrolled
            ? 'max-w-4xl mt-3 md:mt-4 px-4 md:px-6 h-14 md:h-16 rounded-2xl border border-slate-200/80 dark:border-slate-800/80 bg-white/85 dark:bg-slate-900/85 backdrop-blur-2xl shadow-xl shadow-slate-950/5'
            : 'max-w-6xl px-4 md:px-6 h-16 md:h-20 rounded-none border-b border-slate-200/60 dark:border-slate-800/60 bg-white/70 dark:bg-slate-950/70 backdrop-blur-xl'
        )}
      >
        <Link to="/" className="flex items-center gap-3 shrink-0 group" aria-label="Bitmiyo Ana Sayfası">
          <div className="w-9 h-9 md:w-10 md:h-10 overflow-hidden flex items-center justify-center group-hover:scale-105 transition-transform">
            <img src={logo} alt="Bitmiyo Logo - Akıllı Ders Planlayıcısı" className="w-full h-full object-contain" />
          </div>
          <span className="font-extrabold text-xl md:text-2xl tracking-tighter italic text-slate-900 dark:text-white">
            BİTMİYO
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 text-sm font-bold" aria-label="Ana Gezinti Menüsü">
          <a
            {...sectionLinkProps('nasil-calisir')}
            className="px-3.5 py-2 rounded-xl text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors"
          >
            Nasıl Çalışır
          </a>
          <a
            {...sectionLinkProps('ozellikler')}
            className="px-3.5 py-2 rounded-xl text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors"
          >
            Özellikler
          </a>
          <a
            {...sectionLinkProps('sss')}
            className="px-3.5 py-2 rounded-xl text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors"
          >
            SSS
          </a>
          <Link
            to="/blog"
            className="px-3.5 py-2 rounded-xl text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors"
          >
            Blog
          </Link>
          <Link
            to="/puanhesaplama"
            className="px-3.5 py-2 rounded-xl text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors"
          >
            Puan Hesapla
          </Link>
          <a
            href="https://soru.bitmiyo.com.tr"
            target="_blank"
            rel="noopener noreferrer"
            className="px-3.5 py-2 rounded-xl text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors"
          >
            Soru Bankası
          </a>
          <a
            href="https://sinif.bitmiyo.com.tr"
            target="_blank"
            rel="noopener noreferrer"
            className="px-3.5 py-2 rounded-xl text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors"
          >
            Bitmiyo Sınıf
          </a>
        </nav>

        <div className="flex items-center gap-2">
          <SignedOut>
            <SignInButton mode="modal">
              <button className="px-3 md:px-4 py-2 rounded-xl text-sm font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors">
                Giriş Yap
              </button>
            </SignInButton>
            <Button as={Link} to="/yeni" size="sm" className="shadow-md shadow-emerald-600/20 bg-emerald-600 hover:bg-emerald-700 text-white border-none font-extrabold">
              Ücretsiz Başla
            </Button>
          </SignedOut>
          <SignedIn>
            <Button as={Link} to="/anasayfa" size="sm" className="shadow-md shadow-emerald-600/20 bg-emerald-600 hover:bg-emerald-700 text-white border-none font-extrabold">
              Panele Git <ArrowRight size={15} />
            </Button>
          </SignedIn>
        </div>
      </div>
    </header>
  );
}

// ─── Sayfa ───────────────────────────────────────────────────────────────────
export default function Landing() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [annSlideIndex, setAnnSlideIndex] = useState(0);
  const location = useLocation();

  // Baska bir sayfadan #sss, #ozellikler gibi bir hash ile gelindiginde ilgili bolume kaydir
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.hash]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { data, error } = await supabase
          .from('announcements')
          .select('*')
          .eq('is_active', true)
          .order('created_at', { ascending: false });

        if (cancelled) return;
        if (error || !data) return;
        setAnnouncements(data as Announcement[]);
      } catch {
        /* sessizce yoksay */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const latestPosts = useMemo(
    () =>
      [...blogPosts]
        .sort((a, b) => new Date(b.isoDate).getTime() - new Date(a.isoDate).getTime())
        .slice(0, 3),
    []
  );

  // ─── JSON-LD Schema.org Nesneleri ──────────────────────────────────────────
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    'name': 'Bitmiyo',
    'url': 'https://bitmiyo.com.tr',
    'description': 'YouTube ders videolarını ve oynatma listelerini sınava kalan gün sayısına göre kişiselleştirilmiş bir çalışma programına dönüştüren ücretsiz ders planlayıcısı.',
    'applicationCategory': 'EducationalApplication',
    'operatingSystem': 'All',
    'inLanguage': 'tr-TR',
    'offers': {
      '@type': 'Offer',
      'price': '0',
      'priceCurrency': 'TRY'
    },
    'publisher': {
      '@type': 'Organization',
      'name': 'Bitmiyo',
      'url': 'https://bitmiyo.com.tr',
      'logo': 'https://bitmiyo.com.tr/logo.webp'
    }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': FAQS.map((faq) => ({
      '@type': 'Question',
      'name': faq.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': faq.answer
      }
    }))
  };



  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      {
        '@type': 'ListItem',
        'position': 1,
        'name': 'Ana Sayfa',
        'item': 'https://bitmiyo.com.tr/'
      }
    ]
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50/50 dark:bg-slate-950">
      <Helmet>
        <title>Bitmiyo | YouTube Ders Çalışma Planlayıcısı & Takvim Oluşturucu</title>
        <meta
          name="description"
          content="Bitmiyo, YouTube ders videolarını ve oynatma listelerini sınava kalan gün sayısına göre kişiselleştirilmiş bir çalışma programına dönüştürür. YKS, LGS, KPSS, ALES için %100 ücretsiz hedef takibi."
        />
        <meta
          name="keywords"
          content="ders planlayıcı, youtube ders programı, yks çalışma programı, lgs ders takvimi, kpss ders programı, ales çalışma planı, akıllı ders takvimi"
        />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <link rel="canonical" href="https://bitmiyo.com.tr/" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://bitmiyo.com.tr/" />
        <meta property="og:title" content="Bitmiyo | YouTube Ders Çalışma Planlayıcısı" />
        <meta property="og:description" content="YouTube ders videolarını sınava kalan gün sayısına göre akıllı çalışma programına dönüştür. YKS, LGS, KPSS ve ALES için ücretsiz hedef takibi." />
        <meta property="og:image" content="https://bitmiyo.com.tr/bitmiyo-yatay.jpg" />
        <meta property="og:site_name" content="Bitmiyo" />
        <meta property="og:locale" content="tr_TR" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://bitmiyo.com.tr/" />
        <meta name="twitter:title" content="Bitmiyo | Akıllı Ders Planlayıcısı" />
        <meta name="twitter:description" content="YouTube ders playlist'ini ekle, sınava kalan güne göre günlük çalışma takvimi oluştur." />
        <meta name="twitter:image" content="https://bitmiyo.com.tr/bitmiyo-yatay.jpg" />

        {/* Structured Data (Schema.org) */}
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <LandingHeader />

      <main className="flex-1">
        {/* ── HERO SECTION ─────────────────────────────────────────────────────── */}
        <section className="relative overflow-hidden bg-gradient-to-b from-emerald-500/10 via-teal-500/5 to-transparent dark:from-emerald-950/40 dark:via-teal-950/20 dark:to-transparent border-b border-slate-200/60 dark:border-slate-800/60 py-12 md:py-20">
          <div className="absolute top-10 left-1/4 w-72 h-72 bg-emerald-500/15 dark:bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-10 right-1/4 w-80 h-80 bg-teal-500/15 dark:bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />

          <div className="relative max-w-6xl mx-auto px-4 md:px-6 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
            <div className="lg:col-span-7 space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 dark:text-white leading-[1.08]">
                YouTube ders videoların,{' '}
                <span className="bg-gradient-to-r from-emerald-600 via-teal-600 to-indigo-600 dark:from-emerald-400 dark:via-teal-300 dark:to-sky-400 bg-clip-text text-transparent">
                  gerçek bir çalışma programına
                </span>{' '}
                dönüşsün.
              </h1>

              <p className="text-base md:text-lg text-slate-600 dark:text-slate-300 leading-relaxed max-w-xl">
                Takip ettiğin eğitmenin oynatma listesini ekle; Bitmiyo videoları sınava kalan güne
                göre günlere bölsün, günlük hedeflerini takip etsin ve ilerlemeni ölçsün. YKS, LGS, KPSS ve ALES için %100 ücretsiz.
              </p>

              <div className="pt-2 flex flex-wrap items-center gap-3">
                <SignedOut>
                  <Button as={Link} to="/yeni" size="lg" className="shadow-lg shadow-emerald-600/20 bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold border-none px-6">
                    Ücretsiz Başla <ArrowRight size={18} />
                  </Button>
                  <a href="#nasil-calisir">
                    <Button variant="outline" size="lg" className="font-bold border-slate-300 dark:border-slate-700">
                      Nasıl Çalışır?
                    </Button>
                  </a>
                </SignedOut>
                <SignedIn>
                  <Button as={Link} to="/anasayfa" size="lg" className="shadow-lg shadow-emerald-600/20 bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold border-none px-6">
                    Panele Git <ArrowRight size={18} />
                  </Button>
                  <Button as={Link} to="/yeni" variant="outline" size="lg" className="font-bold border-slate-300 dark:border-slate-700">
                    Yeni Plan Oluştur
                  </Button>
                </SignedIn>
              </div>

              <div className="pt-3 flex flex-wrap items-center gap-5 text-xs font-semibold text-slate-500 dark:text-slate-400">
                <span className="flex items-center gap-1.5"><CheckCircle2 size={15} className="text-emerald-500" /> Kurulum veya kredi kartı yok</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 size={15} className="text-emerald-500" /> Sınav tarihine göre otomatik hesaplama</span>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              {/* Yönlendirici Maskot Miyo İllüstrasyonu */}
              <div className="absolute -bottom-10 -left-16 w-36 h-36 hidden xl:block pointer-events-none select-none z-30">
                <img
                  src="/images/maskot/miyo_pointing.webp"
                  alt="Yönlendiren Miyo Maskotu"
                  className="w-full h-full object-contain drop-shadow-[0_10px_20px_rgba(0,0,0,0.15)]"
                />
              </div>

              <div className="relative rounded-3xl p-3 bg-gradient-to-tr from-emerald-500/20 via-teal-500/15 to-indigo-500/20 shadow-2xl border border-white/40 dark:border-slate-800/80 backdrop-blur-xl">
                <div className="rounded-2xl overflow-hidden bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800/80 shadow-inner">
                  <img
                    src="/hero-illustration.webp"
                    alt="Bitmiyo maskotu Miyo ve YouTube ders çalışma takvimi illüstrasyonu"
                    loading="eager"
                    fetchPriority="high"
                    width={600}
                    height={400}
                    className="w-full h-auto object-cover transform hover:scale-[1.02] transition-transform duration-500"
                  />
                </div>

                <div className="absolute bottom-2 left-2 sm:-bottom-4 sm:-left-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3 rounded-2xl shadow-xl flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0">
                    <ListVideo size={18} />
                  </div>
                  <div>
                    <p className="text-xs font-black text-slate-900 dark:text-white">Oynatma Listesi</p>
                    <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400">Ders takvimine dönüşür</p>
                  </div>
                </div>

                <div className="absolute top-2 right-2 sm:-top-4 sm:-right-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3 rounded-2xl shadow-xl flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                    <Target size={18} />
                  </div>
                  <div>
                    <p className="text-xs font-black text-slate-900 dark:text-white">Günlük Hedefler</p>
                    <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400">Sınav tarihine endeksli</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── BİTMİYO NEDİR & NEDEN BİTMİYO? (SICAK GRADYAN DALGA VE SABİT BOYUTLU İNTERAKTİF GÖSTERİM) ── */}
        <section id="ozellikler" className="w-full relative overflow-hidden bg-gradient-to-b from-slate-900 via-[#0e1e33] to-slate-900 text-white py-12 md:py-24 scroll-mt-20 my-0">
          {/* Yumuşak Işık Lekeleri (Arkaplan Derinliği) */}
          <div className="absolute -top-20 -left-16 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none select-none" />
          <div className="absolute top-1/3 -right-24 w-80 h-80 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none select-none" />
          <div className="absolute -bottom-20 left-1/3 w-72 h-72 bg-teal-500/10 rounded-full blur-3xl pointer-events-none select-none" />

          {/* Üst Dalgalı Geçiş (Yumuşak Katmanlı) */}
          <div className="w-full overflow-hidden leading-none absolute top-0 left-0 right-0 pointer-events-none select-none z-10">
            <svg
              className="relative block w-full h-10 md:h-16 text-slate-50 dark:text-slate-950 fill-current"
              viewBox="0 0 1200 120"
              preserveAspectRatio="none"
            >
              <path d="M0,50 C150,20 350,80 600,55 C850,30 1000,70 1200,45 L1200,0 L0,0 Z" opacity="0.5"></path>
              <path d="M0,70 C200,40 400,95 650,70 C900,45 1050,85 1200,65 L1200,0 L0,0 Z"></path>
            </svg>
          </div>

          <div className="relative max-w-6xl mx-auto px-4 md:px-6 z-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              {/* Sol Taraf: Neşeli Yürüyen Asker Miyo Maskotu */}
              <div className="lg:col-span-6 flex justify-center items-center select-none pointer-events-none py-4 lg:py-0">
                <img
                  src="/images/maskot/miyo_marching.webp"
                  alt="Yürüyen Asker Miyo"
                  className="w-full max-w-[200px] sm:max-w-[280px] lg:max-w-md h-auto object-contain drop-shadow-[0_20px_25px_rgba(0,0,0,0.5)]"
                />
              </div>

              {/* Sağ Taraf: Başlık ve 4 Özellik Kartı */}
              <div className="lg:col-span-6 space-y-8">
                <div className="space-y-4">
                  <h2 className="text-3xl md:text-4xl font-black tracking-tight text-white leading-tight">
                    Dağınık oynatma listelerine ve ertelemeye son ver.
                  </h2>
                  <p className="text-slate-300 text-sm md:text-base leading-relaxed">
                    Bitmiyo, YouTube ders videolarını sınav tarihinize göre otomatik böler.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {FEATURES.map((feat) => {
                    return (
                      <div
                        key={feat.id}
                        className="p-5 rounded-2xl border bg-white/[0.04] border-transparent hover:bg-white/[0.08] hover:border-white/10 text-slate-300 transition-all duration-200 select-none space-y-3"
                      >
                        <div className="flex items-center justify-between">
                          <div className="w-12 h-12 flex items-center justify-center">
                            <img
                              src={feat.img}
                              alt={feat.imgAlt}
                              width={44}
                              height={44}
                              loading="lazy"
                              className="w-full h-full object-contain drop-shadow-[0_4px_6px_rgba(0,0,0,0.35)]"
                            />
                          </div>
                        </div>
                        <h3 className="font-black text-sm text-white leading-snug">{feat.title}</h3>
                        <p className="text-xs text-slate-300 leading-normal">{feat.shortDesc}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Alt Dalgalı Geçiş (Yumuşak Katmanlı) */}
          <div className="w-full overflow-hidden leading-none absolute bottom-0 left-0 right-0 pointer-events-none select-none z-10">
            <svg
              className="relative block w-full h-10 md:h-16 text-white dark:text-slate-900 fill-current"
              viewBox="0 0 1200 120"
              preserveAspectRatio="none"
            >
              <path d="M0,90 C250,120 450,60 700,85 C900,105 1050,65 1200,80 L1200,120 L0,120 Z" opacity="0.5"></path>
              <path d="M0,105 C250,135 500,80 750,100 C950,115 1050,85 1200,95 L1200,120 L0,120 Z"></path>
            </svg>
          </div>
        </section>

        {/* ── NASIL ÇALIŞIR? ───────────────────────────────────────────────────── */}
        <section id="nasil-calisir" className="py-12 md:py-24 border-y border-slate-200/60 dark:border-slate-800/60 bg-white dark:bg-slate-900/60 scroll-mt-20">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <h2 className="text-3xl md:text-4xl font-black text-slate-900 dark:text-white">
                Nasıl Çalışır?
              </h2>
              <p className="mt-3 text-sm md:text-base text-slate-600 dark:text-slate-400">
                Bir oynatma listesi ve bir hedef sınav tarihi yeterli — gerisini Bitmiyo hesaplar.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {HOW_IT_WORKS.map((step, i) => (
                <article
                  key={step.title}
                  className="relative p-5 md:p-6 rounded-2xl bg-slate-50/80 dark:bg-slate-950 border border-slate-200/80 dark:border-slate-800/80 shadow-sm hover:shadow-md transition-all hover:-translate-y-1 group flex flex-col justify-between"
                >
                  <div className={cn("absolute top-0 left-6 right-6 h-1 rounded-b-full bg-gradient-to-r", step.color)} />

                  <div>
                    <div className="flex items-center justify-between mb-5 pt-2">
                      <div className={cn("p-3 rounded-xl border", step.badgeBg)}>
                        <step.icon size={22} />
                      </div>
                      <span className="text-3xl font-black text-slate-300 dark:text-slate-800 group-hover:text-emerald-500 transition-colors">
                        0{i + 1}
                      </span>
                    </div>

                    <h3 className="text-base font-black text-slate-900 dark:text-white mb-2">
                      {step.title}
                    </h3>
                    <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ── BİTMİYO EKOSİSTEMİ (ÜÇ PLATFORM ŞEMASI) ────────────────────────────── */}
        <section id="ekosistem" className="py-12 md:py-24 border-b border-slate-200/60 dark:border-slate-800/60 bg-slate-50/60 dark:bg-slate-950/40 scroll-mt-20">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="text-center max-w-2xl mx-auto mb-10">
              <h2 className="text-3xl md:text-4xl font-black text-slate-900 dark:text-white">
                Tek Hesap, Üç Platform
              </h2>
              <p className="mt-3 text-sm md:text-base text-slate-600 dark:text-slate-400">
                Bitmiyo bir ekosistem. Bir kez giriş yaptığında üçünde birden açık kalırsın.
              </p>
            </div>

            {/* Şema: merkez hesap → üç platform */}
            <div className="flex flex-col items-center">
              <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-xs md:text-sm font-black uppercase tracking-wider">
                Bitmiyo Hesabın
              </div>

              {/* Bağlantı çizgileri — kartlarla aynı grid, böylece gap'e rağmen tam hizalı */}
              <div className="hidden md:grid grid-cols-3 gap-6 w-full h-14" aria-hidden="true">
                {[
                  'left-1/2 -right-3',
                  '-left-3 -right-3',
                  '-left-3 right-1/2',
                ].map((seg, i) => (
                  <div key={seg} className="relative">
                    {i === 1 && (
                      <div className="absolute left-1/2 top-0 h-7 w-px -translate-x-1/2 bg-slate-300 dark:bg-slate-700" />
                    )}
                    <div className={cn('absolute top-7 h-px bg-slate-300 dark:bg-slate-700', seg)} />
                    <div className="absolute left-1/2 top-7 bottom-0 w-px -translate-x-1/2 bg-slate-300 dark:bg-slate-700" />
                  </div>
                ))}
              </div>
              <div className="md:hidden h-8 w-px bg-slate-300 dark:bg-slate-700" aria-hidden="true" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
              {ECOSYSTEM.map((site) => {
                const inner = (
                  <>
                    <div className={cn('absolute top-0 left-6 right-6 h-1 rounded-b-full bg-gradient-to-r', site.color)} />
                    <div>
                      <div className={cn('inline-flex p-3 rounded-xl border mb-5 mt-2', site.badgeBg)}>
                        <site.icon size={22} />
                      </div>
                      <h3 className="text-base font-black text-slate-900 dark:text-white">{site.name}</h3>
                      <p className="mt-1 text-[11px] font-bold text-slate-400 dark:text-slate-500">{site.domain}</p>
                      <p className="mt-3 text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                        {site.description}
                      </p>
                      <ul className="mt-4 flex flex-col gap-1.5">
                        {site.points.map((p) => (
                          <li key={p} className="flex items-center gap-2 text-xs font-bold text-slate-700 dark:text-slate-300">
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-300 dark:bg-slate-600 shrink-0" />
                            {p}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-wider text-slate-900 dark:text-white group-hover:gap-2.5 transition-all">
                      Keşfet <ArrowRight size={13} />
                    </span>
                  </>
                );

                const cls = cn(
                  'group relative p-5 md:p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800/80 shadow-sm hover:shadow-md transition-all hover:-translate-y-1 flex flex-col justify-between',
                  site.hover
                );

                return site.external ? (
                  <a key={site.name} href={site.href} target="_blank" rel="noopener noreferrer" className={cls}>
                    {inner}
                  </a>
                ) : (
                  <Link key={site.name} to={site.href} className={cls}>
                    {inner}
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── BİLDİRİM BİTMİYO (DUYURULAR) ───────────────────────────────────────── */}
        {announcements.length > 0 && (() => {
          // İkişerli sayfalama
          const pairs: Announcement[][] = [];
          for (let i = 0; i < announcements.length; i += 2) {
            pairs.push(announcements.slice(i, i + 2));
          }
          const totalPages = pairs.length;

          const renderCard = (a: Announcement, key: string) => {
            const hasLink = a.link_url.trim().length > 0;
            const isExternal = hasLink && /^https?:\/\//i.test(a.link_url);
            const inner = (
              <>
                <div className="aspect-[16/9] w-full overflow-hidden bg-slate-100 dark:bg-slate-800 relative">
                  <img
                    src={a.image_url}
                    alt={a.title}
                    loading="lazy"
                    width={400}
                    height={225}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-5">
                  {a.created_at && (
                    <div className="flex items-center gap-1.5 text-[11px] font-bold text-emerald-600 dark:text-emerald-400 mb-2">
                      <Calendar size={13} className="shrink-0" />
                      <time dateTime={a.created_at}>
                        {new Date(a.created_at).toLocaleDateString('tr-TR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </time>
                    </div>
                  )}
                  <h3 className="text-base font-black text-slate-900 dark:text-white leading-snug line-clamp-2">
                    {a.title}
                  </h3>
                  {hasLink && (
                    <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-wider text-emerald-600 dark:text-emerald-400 group-hover:gap-2.5 transition-all">
                      Detaya Git <ArrowRight size={13} />
                    </span>
                  )}
                </div>
              </>
            );

            const cls = 'p-0 overflow-hidden rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800/80 h-full flex flex-col justify-between';
            const clickableCls = `group ${cls} block transition-all hover:-translate-y-1 hover:shadow-lg hover:border-emerald-500/40`;

            if (!hasLink) return <Card key={key} className={cls}>{inner}</Card>;
            return isExternal ? (
              <Card key={key} as="a" href={a.link_url} target="_blank" rel="noopener noreferrer" className={clickableCls}>{inner}</Card>
            ) : (
              <Card key={key} as={Link} to={a.link_url} className={clickableCls}>{inner}</Card>
            );
          };

          return (
            <section className="py-16 md:py-20 border-b border-slate-200/60 dark:border-slate-800/60 bg-sky-50/50 dark:bg-sky-950/10">
              <div className="max-w-6xl mx-auto px-4 md:px-6">
                {/* Başlık */}
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-black text-slate-900 dark:text-white">
                      Bildirim Bitmiyo
                    </h2>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Sınav dünyasından ve platformdan en güncel duyurular</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                  {/* Sol: İkişerli Carousel */}
                  <div className="lg:col-span-8 flex flex-col gap-4">
                    <div className="overflow-hidden">
                      <div
                        className="flex transition-transform duration-500 ease-in-out"
                        style={{ transform: `translateX(-${annSlideIndex * 100}%)` }}
                      >
                        {pairs.map((pair, pageIdx) => (
                          <div
                            key={pageIdx}
                            className="w-full shrink-0 grid grid-cols-1 sm:grid-cols-2 gap-6"
                          >
                            {pair.map((a) => renderCard(a, `ann-${pageIdx}-${a.id}`))}
                          </div>
                        ))}
                      </div>
                    </div>

                    {totalPages > 1 && (
                      <div className="flex items-center justify-between px-1">
                        <div className="flex items-center gap-2">
                          {pairs.map((_, i) => (
                            <button
                              key={i}
                              onClick={() => setAnnSlideIndex(i)}
                              aria-label={`Sayfa ${i + 1}`}
                              className={cn(
                                'rounded-full transition-all duration-300',
                                i === annSlideIndex
                                  ? 'w-6 h-2.5 bg-emerald-500'
                                  : 'w-2.5 h-2.5 bg-slate-300 dark:bg-slate-700 hover:bg-slate-400 dark:hover:bg-slate-500'
                              )}
                            />
                          ))}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setAnnSlideIndex((p) => Math.max(0, p - 1))}
                            disabled={annSlideIndex === 0}
                            aria-label="Önceki"
                            className="w-9 h-9 rounded-xl flex items-center justify-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm text-slate-600 dark:text-slate-300 hover:text-emerald-600 dark:hover:text-emerald-400 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </button>
                          <button
                            onClick={() => setAnnSlideIndex((p) => Math.min(totalPages - 1, p + 1))}
                            disabled={annSlideIndex === totalPages - 1}
                            aria-label="Sonraki"
                            className="w-9 h-9 rounded-xl flex items-center justify-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm text-slate-600 dark:text-slate-300 hover:text-emerald-600 dark:hover:text-emerald-400 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Sağ: Megafon Tutan Miyo Maskotu */}
                  <div className="lg:col-span-4 flex items-center justify-center select-none pointer-events-none">
                    <img
                      src="/images/maskot/miyo_megaphone.webp"
                      alt="Megafon Tutan Miyo"
                      className="w-full max-w-[280px] lg:max-w-full h-auto object-contain drop-shadow-[0_12px_24px_rgba(0,0,0,0.12)]"
                    />
                  </div>
                </div>
              </div>
            </section>
          );
        })()}


        {/* ── BLOG (REHBERLER) ─────────────────────────────────────────────────── */}
        <section className="py-16 md:py-24 border-b border-slate-200/60 dark:border-slate-800/60 bg-white dark:bg-slate-900/40">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 shrink-0 hidden sm:block select-none pointer-events-none">
                  <img
                    src="/images/maskot/miyo_reading.webp"
                    alt="Rehber Okuyan Miyo Maskotu"
                    className="w-full h-full object-contain drop-shadow-sm"
                  />
                </div>
                <div>
                  <h2 className="text-3xl md:text-4xl font-black text-slate-900 dark:text-white">
                    Sınav hazırlığına dair rehberler
                  </h2>
                </div>
              </div>
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-wider text-emerald-600 dark:text-emerald-400 hover:gap-3 transition-all"
              >
                Tüm Yazılar <ArrowRight size={14} />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {latestPosts.map((post) => (
                <Card
                  key={post.id}
                  as={Link}
                  to={`/blog/${post.slug}`}
                  className="group p-0 overflow-hidden rounded-2xl bg-white dark:bg-slate-950 border border-slate-200/80 dark:border-slate-800/80 block transition-all hover:-translate-y-1 hover:shadow-lg"
                >
                  <div className="aspect-[16/9] w-full overflow-hidden bg-slate-100 dark:bg-slate-800">
                    <img
                      src={post.coverImage}
                      alt={post.title}
                      loading="lazy"
                      width={400}
                      height={225}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">
                      <span className="text-emerald-600 dark:text-emerald-400">{post.category}</span>
                      <span aria-hidden="true">•</span>
                      <span>{post.readTime}</span>
                    </div>
                    <h3 className="text-base font-black text-slate-900 dark:text-white leading-snug line-clamp-2 group-hover:text-emerald-600 transition-colors">
                      {post.title}
                    </h3>
                    <p className="mt-2 text-xs text-slate-600 dark:text-slate-400 leading-relaxed line-clamp-3">
                      {post.description}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-wider text-emerald-600 dark:text-emerald-400 group-hover:gap-2.5 transition-all">
                      Devamını Oku <ArrowRight size={13} />
                    </span>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* ── ÖĞRENCİ YORUMLARI ───────────────────────────────────────────────── */}
        <section className="py-12 md:py-24 border-b border-slate-200/60 dark:border-slate-800/60 bg-purple-50/40 dark:bg-purple-950/10">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-5 mb-10 max-w-3xl mx-auto text-center md:text-left select-none pointer-events-none">
              <div className="w-16 h-16 md:w-24 md:h-24 shrink-0">
                <img
                  src="/images/maskot/miyo_proud.webp"
                  alt="Gururlu Miyo"
                  className="w-full h-full object-contain drop-shadow-md"
                />
              </div>
              <div>
                <h2 className="text-3xl md:text-4xl font-black text-slate-900 dark:text-white">
                  Öğrenci Yorumları
                </h2>
                <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                  Bitmiyo ile ertelemeyi durduran ve hedeflerine ulaşan öğrencilerin başarı hikayeleri.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {TESTIMONIALS.map((t) => (
                <Card key={t.name} className="p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800/80 flex flex-col justify-between shadow-sm hover:shadow-md transition-shadow">
                  <Quote size={24} className="text-purple-500/40 mb-3" />
                  <p className="text-xs md:text-sm text-slate-700 dark:text-slate-300 leading-relaxed flex-1 italic">
                    "{t.text}"
                  </p>
                  <div className="mt-5 pt-4 border-t border-slate-100 dark:border-slate-800/80 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-black text-slate-900 dark:text-white">{t.name}</p>
                      <p className="text-[10px] font-extrabold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 mt-0.5">
                        {t.meta}
                      </p>
                    </div>
                    <div className="w-8 h-8 rounded-full bg-purple-500/10 text-purple-600 dark:text-purple-400 font-bold text-xs flex items-center justify-center">
                      {t.name[0]}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* ── SIKÇA SORULAN SORULAR (SSS / FAQ) ─────────────────────────────────── */}
        <section id="sss" className="py-12 md:py-24 border-b border-slate-200/60 dark:border-slate-800/60 bg-white dark:bg-slate-900/60 scroll-mt-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="text-3xl md:text-4xl font-black text-slate-900 dark:text-white">
                Sıkça Sorulan Sorular
              </h2>
              <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
                Bitmiyo hakkında en çok merak edilen konular
              </p>
            </div>

            <div className="space-y-4">
              {FAQS.map((faq, index) => {
                const isOpen = openFaqIndex === index;
                return (
                  <div
                    key={faq.question}
                    className="rounded-2xl border border-slate-200/80 dark:border-slate-800/80 bg-slate-50/50 dark:bg-slate-950 overflow-hidden transition-colors"
                  >
                    <button
                      onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                      className="w-full p-5 text-left flex items-center justify-between gap-4 font-bold text-base text-slate-900 dark:text-white hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors select-none"
                      aria-expanded={isOpen}
                    >
                      <span>{faq.question}</span>
                      <ChevronDown
                        size={18}
                        className={cn("shrink-0 text-slate-400 transition-transform duration-300", isOpen && "rotate-180 text-emerald-500")}
                      />
                    </button>
                    {isOpen && (
                      <div className="px-5 pb-5 pt-1 text-sm text-slate-600 dark:text-slate-300 leading-relaxed border-t border-slate-200/40 dark:border-slate-800/40">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── SON CTA BANNER ───────────────────────────────────────────────────── */}
        <section className="py-12 md:py-24 bg-gradient-to-b from-slate-900 to-slate-950 text-white relative overflow-hidden">
          {/* Sol Yandan CTA'i İşaret Eden Miyo Maskotu */}
          <div className="absolute left-10 lg:left-24 bottom-1/2 translate-y-1/2 w-28 h-28 hidden md:block pointer-events-none select-none z-10 opacity-70 lg:opacity-100">
            <img
              src="/images/maskot/miyo_pointing.webp"
              alt="Yönlendiren Miyo"
              className="w-full h-full object-contain drop-shadow-[0_8px_16px_rgba(0,0,0,0.3)]"
            />
          </div>

          <div className="max-w-2xl mx-auto px-4 md:px-6 text-center space-y-6 relative z-20">
            <h2 className="text-3xl md:text-5xl font-black text-white leading-tight">
              Bugün bir oynatma listesiyle başla.
            </h2>
            <p className="text-sm md:text-base text-slate-400 max-w-lg mx-auto leading-relaxed">
              Sınava kalan süreyi Bitmiyo hesaplasın, sen sadece günlük hedefini tamamla.
            </p>
            <div className="pt-4 flex flex-wrap items-center justify-center gap-3">
              <SignedOut>
                <Button as={Link} to="/yeni" size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold border-none px-8 py-3.5 shadow-xl shadow-emerald-600/25">
                  Ücretsiz Başla <ArrowRight size={18} />
                </Button>
              </SignedOut>
              <SignedIn>
                <Button as={Link} to="/anasayfa" size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold border-none px-8 py-3.5 shadow-xl shadow-emerald-600/25">
                  Panele Git <ArrowRight size={18} />
                </Button>
              </SignedIn>
            </div>
          </div>
        </section>
      </main>

      {/* ── FOOTER ──────────────────────────────────────────────────────────── */}
      <LandingFooter />
    </div>
  );
}

export function LandingFooter() {
  return (
    <footer className="bg-slate-950 text-white relative">
      {/* Üst Pastel Tek Renk Dalgalı SVG Geçişi */}
      <div className="w-full overflow-hidden leading-none bg-slate-950 -mb-1 pointer-events-none select-none">
        <svg
          className="relative block w-full h-8 md:h-14 text-emerald-800 fill-current"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
        >
          <path d="M0,0 C300,60 600,-20 900,50 C1050,80 1150,30 1200,40 L1200,120 L0,120 Z"></path>
        </svg>
      </div>

      {/* Yeşil / Turkuaz Banner */}
      <div className="bg-emerald-800 py-6 px-4 md:px-6 relative">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-white text-center sm:text-left">
          <div className="flex items-center gap-3">
            <Send size={24} className="text-teal-300 shrink-0" />
            <div>
              <p className="font-black text-sm">Sorun veya Önerin mi Var?</p>
              <p className="text-xs text-teal-100">Bize istediğin an e-posta ile ulaşabilirsin.</p>
            </div>
          </div>
          <a
            href="mailto:bilgi@bitmiyo.com.tr"
            className="px-5 py-2.5 rounded-xl bg-white text-slate-900 font-extrabold text-xs uppercase tracking-wider hover:bg-slate-100 transition-colors shadow-md shrink-0 inline-flex items-center gap-2"
          >
            <Mail size={15} /> bilgi@bitmiyo.com.tr
          </a>
        </div>
      </div>

      {/* Alt Pastel Tek Renk Dalgalı Geçiş */}
      <div className="w-full overflow-hidden leading-none bg-emerald-800 -mb-1 pointer-events-none select-none">
        <svg
          className="relative block w-full h-8 md:h-12 text-slate-950 fill-current"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
        >
          <path d="M0,120 C300,70 600,140 900,80 C1050,50 1150,100 1200,90 L1200,120 L0,120 Z"></path>
        </svg>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 overflow-hidden flex items-center justify-center">
                <img src={logo} alt="Bitmiyo Logo - Akıllı Ders Planlayıcısı" className="w-full h-full object-contain" />
              </div>
              <span className="font-extrabold text-2xl tracking-tighter italic text-white">
                BİTMİYO
              </span>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed max-w-xs">
              YouTube ders videolarını kişiselleştirilmiş bir çalışma programına dönüştüren ücretsiz ders planlayıcısı.
            </p>
          </div>

          <div>
            <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4">
              Platform & Araçlar
            </h3>
            <ul className="space-y-2.5 text-xs font-bold">
              {[
                { to: '/blog', label: 'Blog & Rehberler' },
                { to: '/puanhesaplama', label: 'YKS / LGS Puan Hesapla' },
                { to: '/biz', label: 'Hakkımızda' },
                { to: '/iletisim', label: 'İletişim' },
              ].map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-slate-300 hover:text-emerald-400 transition-colors inline-flex items-center gap-1"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4">
              Sosyal & İletişim
            </h3>
            <ul className="space-y-2.5 text-xs font-bold">
              <li>
                <a
                  href="mailto:bilgi@bitmiyo.com.tr"
                  className="inline-flex items-center gap-2 text-slate-300 hover:text-emerald-400 transition-colors"
                >
                  <Mail size={15} /> bilgi@bitmiyo.com.tr
                </a>
              </li>
              <li>
                <a
                  href="https://youtube.com/@bitmiyo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-slate-300 hover:text-emerald-400 transition-colors"
                >
                  <CirclePlay size={15} /> youtube.com/@bitmiyo
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs font-medium text-slate-500">
            © {new Date().getFullYear()} Bitmiyo. Tüm hakları saklıdır.
          </p>
          <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs font-medium" aria-label="Alt Bilgi Linkleri">
            <Link to="/biz" className="text-slate-400 hover:text-white transition-colors">
              Hakkımızda
            </Link>
            <Link to="/iletisim" className="text-slate-400 hover:text-white transition-colors">
              İletişim
            </Link>
            <Link to="/gizlilik" className="text-slate-400 hover:text-white transition-colors">
              Gizlilik
            </Link>
            <Link to="/kullanim-kosullari" className="text-slate-400 hover:text-white transition-colors">
              Kullanım Koşulları
            </Link>
          </nav>
        </div>
      </div>
    </footer>
  );
}
